'use client';
import {
  PropsWithChildren,
  createContext,
  useContext,
  useCallback,
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import { useUser } from '@auth0/nextjs-auth0/client';
import { UserProfile } from '@/app/_lib/interfaces';
import { usePathname, useRouter } from 'next/navigation';
import OnboardingSplash from '@/app/_components/onboarding/onboardingSplash/dialog';
import {
  getUserProfileKey,
  useUpdateUserProfileMutation,
  useUserProfileFetcher,
} from '@/app/_hooks/fetchers/userProfile';
import { datadogLogs } from '@datadog/browser-logs';

export type UserProfileContextType = {
  userProfile: UserProfile | undefined;
  selectedLanguage: string;
  uploadProfile: (userProfile: Partial<UserProfile>) => Promise<void>;
  isFetching: boolean;
  isUploading: boolean;
  uploadingError: string | null;
  setSelectedLanguage: Dispatch<SetStateAction<string>>;
  googleToken: string | undefined;
  setGoogleToken: (token?: string) => void;
};

export const UserProfileContext = createContext<UserProfileContextType | null>(
  null
);

const UserProfileProvider: React.FC<PropsWithChildren> = ({
  children,
}: PropsWithChildren) => {
  const { user } = useUser();
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [showOnboardingDialog, setShowOnboardingDialog] = useState(false);
  const [googleToken, setGoogleToken] = useState<string | undefined>();

  const router = useRouter();
  const pathname = usePathname();

  const isRedeemPage = pathname.includes('/redeem');
  const isAffiliateSignupPage = pathname.includes('/affiliate');

  const swrKey = getUserProfileKey(`${user?.sub}`);

  const {
    data: userProfile,
    isLoading: isFetching,
    mutate,
  } = useUserProfileFetcher(`${user?.sub}`, !user || isAffiliateSignupPage);

  const {
    trigger,
    isMutating: isUploading,
    error: uploadingError,
  } = useUpdateUserProfileMutation(`${user?.sub}`);

  useEffect(() => {
    setShowOnboardingDialog(
      !isRedeemPage &&
        !isAffiliateSignupPage &&
        !!(userProfile && !userProfile?.user_flags.onboarding_informed)
    );
  }, [userProfile, isRedeemPage]);

  const uploadProfile = useCallback(
    async (userProfile: Partial<UserProfile>) => {
      try {
        datadogLogs.logger.info(
          `Uploading user profile: ${JSON.stringify(userProfile)}`
        );
        await trigger(userProfile);
        mutate();
      } catch (e) {
        datadogLogs.logger.error(
          // @ts-ignore
          `Failed to update user profile: ${JSON.stringify(e.response.data?.message)}`
        );
      }
    },
    [swrKey]
  );

  const onOnboardingSubmit = useCallback(
    async (data: any) => {
      const { school, major, graduation_year, activity_types, language } = data;

      await uploadProfile({
        user_data: {
          student_info: {
            school: school?.label,
            graduation_year: parseInt(graduation_year),
            major,
          },
          activity_types: activity_types,
        },
        user_preferences: {
          preferred_language: language,
        },
        user_flags: {
          preferred_language_informed: true,
          onboarding_informed: true,
        },
      });
      //keep the user on the confetti screen for 2s so that he can enjoy confetti
      setTimeout(() => {
        setShowOnboardingDialog(false);
        router.push(`/`);
      }, 2000);
    },
    [setShowOnboardingDialog, uploadProfile]
  );

  return (
    <UserProfileContext.Provider
      value={{
        userProfile,
        uploadProfile,
        selectedLanguage,
        isFetching,
        isUploading,
        uploadingError,
        setSelectedLanguage,
        googleToken,
        setGoogleToken,
      }}
    >
      {children}

      <OnboardingSplash
        isOpen={showOnboardingDialog}
        onClose={onOnboardingSubmit}
      />
    </UserProfileContext.Provider>
  );
};

const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error(`useUserProfile must be used within a UserProfileProvider`);
  }
  return context;
};

export default UserProfileProvider;
export { useUserProfile };
